/**
 * Modal - Dialog Overlay Component
 *
 * Compound component for modal dialogs.
 * Uses context to share state between sub-components.
 *
 * @example
 * <Modal.Root open={isOpen} onOpenChange={setIsOpen}>
 *   <Modal.Content>
 *     <Modal.Header>
 *       <Modal.Title>Confirm Action</Modal.Title>
 *       <Modal.Close />
 *     </Modal.Header>
 *     <Modal.Body>Are you sure?</Modal.Body>
 *     <Modal.Footer>
 *       <Button onClick={handleConfirm}>Confirm</Button>
 *     </Modal.Footer>
 *   </Modal.Content>
 * </Modal.Root>
 */
import { createContext, useContext, useEffect, forwardRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Box, Text } from "@/shared/ui/base";
import { cn } from "@/shared/lib/utils/cn";
import { useEscapeKey } from "@/shared/hooks";
import type { BaseComponentProps } from "@/shared/ui/base";

/**
 * Modal Context
 */
interface ModalContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

function useModalContext() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("Modal compound components must be used within Modal.Root");
  }
  return context;
}

/**
 * Modal.Root - Root container with context
 */
interface ModalRootProps extends BaseComponentProps {
  /** Modal open state */
  open: boolean;
  /** Open state change handler */
  onOpenChange: (open: boolean) => void;
}

function ModalRoot({ open, onOpenChange, children }: ModalRootProps) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  if (!open) return null;

  return (
    <ModalContext.Provider value={{ open, onOpenChange }}>
      {children}
    </ModalContext.Provider>
  );
}

/**
 * Modal.Overlay - Background overlay
 */
interface ModalOverlayProps extends BaseComponentProps {
  /** Disable clicking overlay to close */
  disableClickOutside?: boolean;
}

const ModalOverlay = forwardRef<HTMLDivElement, ModalOverlayProps>(
  ({ disableClickOutside, className, ...props }, ref) => {
    const { onOpenChange } = useModalContext();

    const handleClick = () => {
      if (!disableClickOutside) {
        onOpenChange(false);
      }
    };

    return (
      <Box
        ref={ref}
        className={cn(
          "fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4",
          className,
        )}
        onClick={handleClick}
        {...props}
      />
    );
  },
);

ModalOverlay.displayName = "Modal.Overlay";

/**
 * Modal.Content - Main modal content container
 */
interface ModalContentProps extends BaseComponentProps {
  /** Max width variant */
  size?: "sm" | "md" | "lg" | "xl" | "full";
}

const ModalContent = forwardRef<HTMLDivElement, ModalContentProps>(
  ({ size = "md", className, children, ...props }, ref) => {
    const { onOpenChange } = useModalContext();

    // Close on Escape key
    useEscapeKey(() => onOpenChange(false));

    const sizeClasses = {
      sm: "max-w-sm",
      md: "max-w-2xl",
      lg: "max-w-4xl",
      xl: "max-w-6xl",
      full: "max-w-full",
    };

    // Prevent clicks inside content from closing modal
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    return (
      <Box
        ref={ref}
        className={cn(
          "bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-y-auto",
          sizeClasses[size],
          className,
        )}
        onClick={handleClick}
        {...props}
      >
        {children}
      </Box>
    );
  },
);

ModalContent.displayName = "Modal.Content";

/**
 * Modal.Header - Sticky header section
 */
const ModalHeader = forwardRef<HTMLDivElement, BaseComponentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <Box
        ref={ref}
        className={cn(
          "sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10",
          className,
        )}
        {...props}
      >
        {children}
      </Box>
    );
  },
);

ModalHeader.displayName = "Modal.Header";

/**
 * Modal.Title - Modal title text
 */
const ModalTitle = forwardRef<HTMLHeadingElement, BaseComponentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <Text
        as="h2"
        ref={ref}
        variant="h3"
        className={cn("text-gray-900", className)}
        {...props}
      >
        {children}
      </Text>
    );
  },
);

ModalTitle.displayName = "Modal.Title";

/**
 * Modal.Close - Close button
 */
const ModalClose = forwardRef<HTMLButtonElement, BaseComponentProps>(
  ({ className, ...props }, ref) => {
    const { onOpenChange } = useModalContext();

    return (
      <Box
        as="button"
        type="button"
        ref={ref}
        onClick={() => onOpenChange(false)}
        className={cn(
          "p-2 hover:bg-gray-100 rounded-lg transition-colors",
          className,
        )}
        aria-label="Close"
        {...props}
      >
        <X className="w-5 h-5 text-gray-500" />
      </Box>
    );
  },
);

ModalClose.displayName = "Modal.Close";

/**
 * Modal.Body - Content body section
 */
const ModalBody = forwardRef<HTMLDivElement, BaseComponentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <Box ref={ref} className={cn("p-6", className)} {...props}>
        {children}
      </Box>
    );
  },
);

ModalBody.displayName = "Modal.Body";

/**
 * Modal.Footer - Footer section for actions
 */
const ModalFooter = forwardRef<HTMLDivElement, BaseComponentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <Box
        ref={ref}
        className={cn(
          "flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50",
          className,
        )}
        {...props}
      >
        {children}
      </Box>
    );
  },
);

ModalFooter.displayName = "Modal.Footer";

/**
 * Modal.Portal - Renders children into document.body via React Portal
 *
 * Use this to escape parent stacking contexts and ensure the modal
 * covers the full viewport regardless of ancestor overflow/positioning.
 *
 * @example
 * <Modal.Root open={isOpen} onOpenChange={setIsOpen}>
 *   <Modal.Portal>
 *     <Modal.Overlay>
 *       <Modal.Content>...</Modal.Content>
 *     </Modal.Overlay>
 *   </Modal.Portal>
 * </Modal.Root>
 */
interface ModalPortalProps {
  /** Target container (defaults to document.body) */
  container?: HTMLElement;
  /** Child content to portal */
  children: React.ReactNode;
}

function ModalPortal({ container, children }: ModalPortalProps) {
  // Ensure we're in a browser environment
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(children, container || document.body);
}

ModalPortal.displayName = "Modal.Portal";

/**
 * Export Modal compound component
 */
export const Modal = {
  Root: ModalRoot,
  Portal: ModalPortal,
  Overlay: ModalOverlay,
  Content: ModalContent,
  Header: ModalHeader,
  Title: ModalTitle,
  Close: ModalClose,
  Body: ModalBody,
  Footer: ModalFooter,
};
